/* eslint-disable */
import React, { useState, useRef } from 'react';
import { Auth } from 'aws-amplify';
import {
  Form,
  Input,
  Radio,
  Checkbox,
  DatePicker,
  InputNumber,
  TimePicker,
} from 'antd';
import { Form as BootstrapForm } from 'react-bootstrap';
import moment from 'moment';
// My Components
import { camelize } from './utils';
import config from '../config';
import { user } from '../lib/authLib';
import { s3Upload } from '../lib/awsLib';
import LoaderButton from '../components/LoaderButton';
import './ClassForm.scss';

export default function ClassForm({
  defaultForm = {},
  isEditingClass = false,
  getClasses,
  closeClassForm,
  allCardsData,
  setClassesUI,
  allDisplayedCardsData,
}) {
  const file = useRef(null);
  const [ form ] = Form.useForm();
  const [showSwirlyAndDisableSubmitBtn, setShowSwirlyAndDisableSubmitBtn] = useState(false);
  const americanDatePickerFormat = 'MM/DD/YYYY';

  let createNew = false;

  function setDefaultClassTime(timeIn24hrDay) {
    const defaultFormatForAntd = 'YYYY-MM-DD';

    return moment(
      moment().format(defaultFormatForAntd) + ' ' + timeIn24hrDay,
      [moment.ISO_8601, 'HH:mm a']
    );
  }

  const today = new Date();
  const tomorrow = new Date(today).setDate(today.getDate() + 1);
  const blankFormVals = {
    title: '',
    price: '',
    instructors: '',
    daysOfWeek: [],
    startDate: moment(today),
    endDate: moment(tomorrow),
    classClosedDate: moment(today).add(21, 'days'),
    startTime: setDefaultClassTime('18:00'),
    endTime: setDefaultClassTime('20:00'),
    content: '',
    maxClassSize: 100,
    maxImbalance: 1,
    leadFollowClass: true,
    published: false,
  };

  async function onFinish(formData) {
    const { img, uuid, attendees } = defaultForm;
    let successfulResponse = null;
    const dateFormat = 'MM-DD-YYYY';
    const timeFormat = 'hh mm a';
    formData.price = (formData.price).toString();
    formData.startTime = formData.startTime.format(timeFormat);
    formData.endTime = formData.endTime.format(timeFormat);
    formData.startDate = formData.startDate.format(dateFormat);
    formData.endDate = formData.endDate.format(dateFormat);
    formData.classClosedDate = formData.classClosedDate.format(dateFormat);
    formData.published = formData.published && !(createNew && isEditingClass); // Never publish a class with the duplicate button

    setShowSwirlyAndDisableSubmitBtn(true);

    if (!createNew && uuid) {
      formData.uuid = uuid;
    }

    if (!createNew && attendees) {
      formData.attendees = attendees;
    }

    try {
      if (file.current) {
        const { fullImgUrl } = await s3Upload(file.current);
        formData.img = fullImgUrl;
      } else if (img) {
        formData.img = img;
      }

      const currentUserSession = await Auth.currentSession();

      const response = await window.fetch(`${config.serverUrl}classes/create/${user[0].email}`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'accesstoken': currentUserSession.getAccessToken().getJwtToken(),
        },
        body: JSON.stringify(formData),
      });

      successfulResponse = await response.json();

      if (!createNew) { // re-set class data locally to show the update
        setClassesUI(
          allCardsData.map(replaceClassIfUpdatedCallback),
          allDisplayedCardsData.map(replaceClassIfUpdatedCallback)
        );

        function replaceClassIfUpdatedCallback(individualClass) {
          if (successfulResponse.uuid === individualClass.uuid) {
            return successfulResponse;
          }

          return individualClass;
        }
      }
    } catch (error) {
      console.error('Error!\n', error);
    } finally {
      setShowSwirlyAndDisableSubmitBtn(false);
      await getClasses();
      // TODO Show success popover here!
      closeClassForm();
      if (successfulResponse && successfulResponse.uuid) {
        setTimeout(() => {
          document
            .getElementById(successfulResponse.uuid)
            .scrollIntoView({
              block: "center"
            });
        }, 0);
      }
      if (!isEditingClass) {
        form.setFieldsValue(blankFormVals);
      }
    }
  }

  function onFinishFailed(e) {
    console.error("onFinishFailed", e);
  }

  function dateOrTimePicker(temporalLocation, pickerElement) {
    const generatePicker = (datePosition, temporal, picker) => (
      <Form.Item
        key={`${datePosition}-${temporal}`}
        className={`${datePosition.toLowerCase()}-${temporal.toLowerCase()}`}
        label={`${datePosition} ${temporal}`}
        name={camelize(`${datePosition}${temporal}`)}
        rules={[
          {
            required: true,
            message: `Please input a ${datePosition.toLowerCase()} ${temporal.toLowerCase()}.`,
          },
        ]}
      >
        {picker}
      </Form.Item>
    );

    return [
      'Start',
      'End',
    ].map((position) => generatePicker(position, temporalLocation, pickerElement));
  }

  function handleFileChange(event) {
    file.current = event.target.files[0];
  }

  function defaultFormVals(defaultFormValues) {
    if (Object.keys(defaultFormValues).length > 1) {
      let { startTime, endTime, startDate, endDate, classClosedDate } = defaultFormValues;
      const timeFormatPattern = 'hh mm a';

      return Object.assign({},
        defaultFormValues,
        {
          startDate: moment(startDate, americanDatePickerFormat),
          endDate: moment(endDate, americanDatePickerFormat),
          startTime: moment(startTime, timeFormatPattern),
          endTime: moment(endTime, timeFormatPattern),
          classClosedDate: moment(classClosedDate, americanDatePickerFormat),
        }
      );
    }

    return blankFormVals;
  }

  return (
    <Form
      form={form}
      className="ClassForm"
      name="basic"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      initialValues={defaultFormVals(defaultForm)}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Form.Item
        label="Class Title: "
        name="title"
        rules={[
          {
            required: true,
            message: 'Please input your class title',
          },
        ]}
      >
        <Input />
      </Form.Item>

      <div className="image-upload-container-outer">
        <div className="image-upload-container-inner">
          <BootstrapForm.Group controlId="file">
            <BootstrapForm.Label>Upload Image For Class</BootstrapForm.Label>
            <BootstrapForm.Control onChange={handleFileChange} type="file" />
          </BootstrapForm.Group>
        </div>
      </div>

      <Form.Item
        label="Price: "
        name="price"
        rules={[
          {
            required: true,
            message: 'Please Input a price',
          },
        ]}
      >
        <InputNumber
          style={{ width: '130px' }}
          formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
        />
      </Form.Item>

      <Form.Item
        label="Instructor Name(s): "
        name="instructors"
        rules={[
          {
            required: true,
            message: 'Please input your name(s)',
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Weekdays for the class: "
        name="daysOfWeek"
        rules={[
          {
            required: true,
            message: 'Please input your selected day(s) for the class',
          },
        ]}
      >
        <Checkbox.Group
          options={[
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday',
          ].map((day) => (
            {
              label: day,
              value: day,
            }
          ))}
        />
      </Form.Item>

      {
        dateOrTimePicker(
          'Date',
          <DatePicker
            dropdownClassName="clickable-dropdown-for-picker"
            format={new Array(2).fill(americanDatePickerFormat)} />
        )
      }

      {
        dateOrTimePicker(
          'Time',
          <TimePicker
            use12Hours
            popupClassName="clickable-dropdown-for-picker"
            format="h:mm a" />
        )
      }

      <Form.Item
        label="Class Closed Date: "
        name="classClosedDate"
        rules={[
          {
            required: true,
            message: 'Please choose a date to close enrollment for the class.',
          },
        ]}
      >
        <DatePicker
          dropdownClassName="clickable-dropdown-for-picker"
          format={new Array(2).fill(americanDatePickerFormat)} />
      </Form.Item>

      <Form.Item
        label="Class Description: "
        name="content"
        rules={[
          {
            required: true,
            message: 'Please write a description for your class.',
          },
        ]}
      >
        <Input.TextArea />
      </Form.Item>

      <Form.Item
        label="Max Class Size: "
        name="maxClassSize"
        rules={[
          {
            required: true,
            message: 'Please enter a max class size.',
          },
        ]}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        label="Max Lead/Follow Imbalance: "
        name="maxImbalance"
        rules={[
          {
            required: true,
            message: 'Please enter your max lead/follow imbalance.',
          },
        ]}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        label="Is this a class with leads and follows? "
        name="leadFollowClass"
        rules={[
          {
            required: true,
            message: 'Please select if this class has leads and follows.',
          },
        ]}
      >
        <Radio.Group>
          <Radio value>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="Publish class to the public?"
        name="published"
        rules={[
          {
            required: true,
            message: 'Should we make this class public?',
          },
        ]}
      >
        <Radio.Group>
          <Radio value>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item wrapperCol={{ offset: isEditingClass ? 7 : 8, span: 16 }}>
        <LoaderButton
          type="submit"
          variant="primary"
          onClick={ () => createNew = !isEditingClass }
          isLoading={showSwirlyAndDisableSubmitBtn}
          disabled={showSwirlyAndDisableSubmitBtn}
        >
          {
            isEditingClass
              ? 'Update'
              : 'Create New Class'
          }
        </LoaderButton>
        <LoaderButton
          type="submit"
          variant="secondary"
          onClick={ () => createNew = true }
          isLoading={showSwirlyAndDisableSubmitBtn}
          disabled={showSwirlyAndDisableSubmitBtn}
          style={{ marginLeft: 10 }}
        >
          Duplicate
        </LoaderButton>
      </Form.Item>
    </Form>
  );
}
